import * as indicatorTypes from "@chartLib/lib/indicator";
import * as seriesTypes from "@chartLib/lib/series";
import * as tooltipTypes from "@chartLib/lib/tooltip";
import * as coordinateTypes from "@chartLib/lib/coordinates";
import {
  WindowSizeType,
  SourcePathType,
  NumericPropertyType,
  StrokeType,
  FillType,
  MultiplierType,
  MovingAverageType,
  BooleanType,
  ColorType,
} from "../PropertyTypes";

const volatilityRegistry = {
  displayName: "Volatilidade",
  indicators: {
    donchianChannel: {
      displayName: "Canal de Donchian (Donchian Channel)",
      key: "donchianChannel",
      type: indicatorTypes.donchianChannel,
      series: seriesTypes.DonchianSeries,
      coordinate: coordinateTypes.CurrentCoordinate,
      requireNewAxis: false,
      drawOnMainChart: true,
      defaultOptions: [
        NumericPropertyType({
          name: "highPeriod",
          displayName: "Período Máxima",
          min: 1,
          max: 100,
          defaultValue: 20,
        }),
        NumericPropertyType({
          name: "lowPeriod",
          displayName: "Período Mínima",
          min: 1,
          max: 100,
          defaultValue: 20,
        }),
        BooleanType({
          name: "channelFill",
          displayName: "Preenchimento",
          defaultValue: true,
          trueLabel: "Sim",
          falseLabel: "Não",
        }),
      ],
      defaultOptionsForAppearance: [
        StrokeType({
          name: "strokeTop",
          displayName: "Cor Superior",
          defaultValue: {
            color: "#2196F3",
            width: 1,
            opacity: 1,
            dash: "Solid",
          },
        }),
        StrokeType({
          name: "strokeMiddle",
          displayName: "Cor Mediana",
          defaultValue: {
            color: "#FF9800",
            width: 1,
            opacity: 1,
            dash: "Solid",
          },
        }),
        StrokeType({
          name: "strokeBottom",
          displayName: "Cor Inferior",
          defaultValue: {
            color: "#2196F3",
            width: 1,
            opacity: 1,
            dash: "Solid",
          },
        }),
        FillType({
          name: "fill",
          displayName: "Cor Preenchimento",
          defaultValue: { color: "#2196F3", opacity: 0.1 },
        }),
      ],
      labelConfig: {
        lines: [
          { key: "top", colorPath: "strokeTop.color" },
          { key: "middle", colorPath: "strokeMiddle.color" },
          { key: "bottom", colorPath: "strokeBottom.color" },
        ],
      },
      tooltip: tooltipTypes.DonchianChannelTooltip,
      renderKey: "donchianChannel",
      description:
        "Canal formado pelos máximos e mínimos de períodos especificados. Linha superior = máximo do período alto, linha inferior = mínimo do período baixo, mediana = média das duas.",
    },
    atrbands: {
      displayName: "Bandas ATR",
      key: "atrbands",
      type: indicatorTypes.atrbands,
      series: seriesTypes.ATRBandsSeries,
      coordinate: coordinateTypes.CurrentCoordinate,
      requireNewAxis: false,
      defaultOptions: [
        WindowSizeType({ defaultValue: 5 }),
        SourcePathType({ defaultValue: "close" }),
        NumericPropertyType({
          name: "multiplier",
          displayName: "Multiplicador",
          defaultValue: 3,
          min: 0.1,
          max: 5,
          step: 0.1,
        }),
      ],
      defaultOptionsForAppearance: [
        StrokeType({
          name: "strokeTop",
          displayName: "Cor Superior",
          defaultValue: {
            color: "#964B00",
            width: 1,
            opacity: 1,
            dash: "Solid",
          },
        }),
        StrokeType({
          name: "strokeBottom",
          displayName: "Cor Inferior",
          defaultValue: {
            color: "#964B00",
            width: 1,
            opacity: 1,
            dash: "Solid",
          },
        }),
        FillType({
          name: "fill",
          displayName: "Cor Preenchimento",
          defaultValue: { color: "#4682B4", opacity: 0.1 },
        }),
      ],
      labelConfig: {
        lines: [
          { key: "top", colorPath: "strokeTop.color" },
          { key: "middle", colorPath: "strokeMiddle.color" },
          { key: "bottom", colorPath: "strokeBottom.color" },
        ],
      },
      tooltip: tooltipTypes.ATRBandsTooltip,
      renderKey: "atrBands", // Critical for tooltip functionality
    },
    keltner: {
      displayName: "Canal de Keltner",
      key: "keltner",
      type: indicatorTypes.keltner,
      series: seriesTypes.ATRBandsSeries,
      coordinate: coordinateTypes.CurrentCoordinate,
      requireNewAxis: false,
      drawOnMainChart: true,
      defaultOptions: [
        WindowSizeType({ name: "period", defaultValue: 50 }),
        SourcePathType({ name: "sourcePath", defaultValue: "close" }),
        NumericPropertyType({
          name: "multiplier",
          displayName: "Multiplicador ATR",
          defaultValue: 5,
          min: 0.1,
          step: 0.1,
        }),
        MovingAverageType({ name: "movingAverageType", defaultValue: "ema" }),
        BooleanType({
          name: "channelFill",
          displayName: "Preenchimento",
          defaultValue: true,
        }),
      ],
      defaultOptionsForAppearance: [
        StrokeType({
          name: "strokeTop",
          displayName: "Cor Superior",
          defaultValue: {
            color: "#1f77b4",
            width: 1,
            opacity: 1,
            dash: "Solid",
          },
        }),
        StrokeType({
          name: "strokeMiddle",
          displayName: "Cor Média",
          defaultValue: {
            color: "#ff7f0e",
            width: 1,
            opacity: 1,
            dash: "Solid",
          },
        }),
        StrokeType({
          name: "strokeBottom",
          displayName: "Cor Inferior",
          defaultValue: {
            color: "#1f77b4",
            width: 1,
            opacity: 1,
            dash: "Solid",
          },
        }),
        FillType({
          name: "fill",
          displayName: "Cor Preenchimento",
          defaultValue: { color: "#1f77b4", opacity: 0.1 },
        }),
      ],
      labelConfig: {
        lines: [
          { key: "top", colorPath: "strokeTop.color" },
          { key: "middle", colorPath: "strokeMiddle.color" },
          { key: "bottom", colorPath: "strokeBottom.color" },
        ],
      },
      tooltip: tooltipTypes.KeltnerTooltip,
      renderKey: "keltner",
      description:
        "Canal de Keltner baseado em EMA e desvio via ATR, exibindo banda superior, mediana e inferior.",
    },
    starcBands: {
      displayName: "STARC Bands",
      key: "starcBands",
      type: indicatorTypes.starcBands,
      series: seriesTypes.ATRBandsSeries,
      coordinate: coordinateTypes.CurrentCoordinate,
      requireNewAxis: false,
      drawOnMainChart: true,
      defaultOptions: [
        NumericPropertyType({
          name: "period",
          displayName: "Período ATR",
          defaultValue: 15,
          min: 1,
          max: 100,
        }),
        NumericPropertyType({
          name: "maPeriod",
          displayName: "Período MA",
          defaultValue: 5,
          min: 1,
          max: 100,
        }),
        NumericPropertyType({
          name: "multiplier",
          displayName: "Multiplicador",
          defaultValue: 1.3,
          min: 0.1,
          max: 10,
          step: 0.1,
        }),
        BooleanType({
          name: "channelFill",
          displayName: "Preenchimento",
          defaultValue: true,
        }),
      ],
      defaultOptionsForAppearance: [
        StrokeType({
          name: "strokeTop",
          displayName: "Cor Superior",
          defaultValue: {
            color: "#2196F3",
            width: 1,
            opacity: 1,
            dash: "Solid",
          },
        }),
        StrokeType({
          name: "strokeMiddle",
          displayName: "Cor Mediana",
          defaultValue: {
            color: "#2196F3",
            width: 1,
            opacity: 1,
            dash: "Solid",
          },
        }),
        StrokeType({
          name: "strokeBottom",
          displayName: "Cor Inferior",
          defaultValue: {
            color: "#2196F3",
            width: 1,
            opacity: 1,
            dash: "Solid",
          },
        }),
        FillType({
          name: "fill",
          displayName: "Cor Preenchimento",
          defaultValue: { color: "#2196F3", opacity: 0.1 },
        }),
      ],
      labelConfig: {
        lines: [
          { key: "top", colorPath: "strokeTop.color" },
          { key: "middle", colorPath: "strokeMiddle.color" },
          { key: "bottom", colorPath: "strokeBottom.color" },
        ],
      },
      tooltip: tooltipTypes.STARCBandsTooltip,
      renderKey: "starcBands",
      description:
        "STARC Bands: canal baseado em SMA (MA Period) ± (Multiplier × ATR(Period)). Banda superior = SMA + (Mult × ATR), mediana = SMA, banda inferior = SMA - (Mult × ATR).",
    },
    maEnvelope: {
      displayName: "Moving Average Envelope",
      key: "maEnvelope",
      type: indicatorTypes.maEnvelope,
      series: seriesTypes.ATRBandsSeries,
      coordinate: coordinateTypes.CurrentCoordinate,
      requireNewAxis: false,
      drawOnMainChart: true,
      defaultOptions: [
        WindowSizeType({
          name: "period",
          displayName: "Período",
          defaultValue: 50,
          min: 1,
          max: 200,
        }),
        SourcePathType({
          name: "sourcePath",
          displayName: "Campo",
          defaultValue: "close",
        }),
        MovingAverageType({
          name: "movingAverageType",
          displayName: "Tipo MA",
          defaultValue: "sma",
        }),
        MovingAverageType({
          name: "shiftType",
          displayName: "Tipo Desvio",
          options: [
            { value: "percent", label: "Percentual" },
            { value: "points", label: "Pontos" },
          ],
          defaultValue: "percent",
        }),
        NumericPropertyType({
          name: "shift",
          displayName: "Desvio",
          defaultValue: 5,
          min: 0.1,
          max: 100,
          step: 0.1,
        }),
        BooleanType({
          name: "channelFill",
          displayName: "Preenchimento",
          defaultValue: true,
        }),
      ],
      defaultOptionsForAppearance: [
        StrokeType({
          name: "strokeTop",
          displayName: "Cor Superior",
          defaultValue: {
            color: "#2196F3",
            width: 1,
            opacity: 1,
            dash: "Solid",
          },
        }),
        StrokeType({
          name: "strokeMiddle",
          displayName: "Cor Mediana",
          defaultValue: {
            color: "#FF9800",
            width: 1,
            opacity: 1,
            dash: "Solid",
          },
        }),
        StrokeType({
          name: "strokeBottom",
          displayName: "Cor Inferior",
          defaultValue: {
            color: "#2196F3",
            width: 1,
            opacity: 1,
            dash: "Solid",
          },
        }),
        FillType({
          name: "fill",
          displayName: "Cor Preenchimento",
          defaultValue: { color: "#2196F3", opacity: 0.1 },
        }),
      ],
      labelConfig: {
        lines: [
          { key: "top", colorPath: "strokeTop.color" },
          { key: "middle", colorPath: "strokeMiddle.color" },
          { key: "bottom", colorPath: "strokeBottom.color" },
        ],
      },
      tooltip: tooltipTypes.MAEnvelopeTooltip,
      renderKey: "maEnvelope",
      description:
        "Moving Average Envelope: canal baseado em MA(período, tipo) ± desvio. Modo Percentual: Top = MA × (1 + desvio/100), Bottom = MA × (1 - desvio/100). Modo Pontos: Top = MA + desvio, Bottom = MA - desvio.",
    },
    highLow: {
      displayName: "High Low (HHV/LLV)",
      key: "highLow",
      type: indicatorTypes.highLow,
      series: seriesTypes.HighLowSeries,
      coordinate: coordinateTypes.CurrentCoordinate,
      requireNewAxis: false,
      drawOnMainChart: true,
      defaultOptions: [
        WindowSizeType({
          name: "period",
          displayName: "Período",
          defaultValue: 14,
          min: 1,
          max: 200,
        }),
      ],
      defaultOptionsForAppearance: [
        StrokeType({
          name: "strokeHigh",
          displayName: "Linha HHV",
          defaultValue: {
            color: "#4CAF50",
            width: 1,
            opacity: 1,
            dash: "Solid",
          },
        }),
        StrokeType({
          name: "strokeLow",
          displayName: "Linha LLV",
          defaultValue: {
            color: "#F44336",
            width: 1,
            opacity: 1,
            dash: "Solid",
          },
        }),
      ],
      labelConfig: {
        lines: [
          { key: "high", colorPath: "strokeHigh.color" },
          { key: "low", colorPath: "strokeLow.color" },
        ],
      },
      tooltip: tooltipTypes.HighLowTooltip,
      renderKey: "highLow",
      description:
        "HHV/LLV: mostra o máximo (HHV - Highest High Value) e mínimo (LLV - Lowest Low Value) do período. HHV = Max(High, período), LLV = Min(Low, período).",
    },
    highLowMa: {
      displayName: "High & Low (Médias de Máx/Mín)",
      key: "highLowMa",
      type: indicatorTypes.highLowMa,
      series: seriesTypes.HighLowMaSeries,
      coordinate: coordinateTypes.CurrentCoordinate,
      requireNewAxis: false,
      isPriceAxis: true,
      drawOnMainChart: true,
      renderKey: "highLowMa",
      defaultOptions: [
        // min/max fiéis ao AddParam do engine (0–1000; N=0 = média cumulativa).
        NumericPropertyType({
          name: "mHigh",
          displayName: "Média das Máximas",
          min: 0,
          max: 1000,
          defaultValue: 20,
        }),
        NumericPropertyType({
          name: "mLow",
          displayName: "Média das Mínimas",
          min: 0,
          max: 1000,
          defaultValue: 20,
        }),
        NumericPropertyType({
          name: "deslocV",
          displayName: "Deslocamento Vertical (%)",
          min: 0,
          max: 1000,
          defaultValue: 0,
        }),
      ],
      defaultOptionsForAppearance: [
        // Cores default = engine Silverlight (2 séries sem SetAttrs →
        // 1ª Yellow, 2ª Red — mesmo padrão do RSICutler). Ver LICOES.md.
        StrokeType({
          name: "strokeHigh",
          displayName: "Linha H",
          defaultValue: {
            color: "#FFFF00",
            dash: "Solid",
            width: 1,
            opacity: 1,
          },
        }),
        StrokeType({
          name: "strokeLow",
          displayName: "Linha L",
          defaultValue: {
            color: "#FF0000",
            dash: "Solid",
            width: 1,
            opacity: 1,
          },
        }),
      ],
      labelConfig: {
        lines: [
          { key: "high", colorPath: "strokeHigh.color" },
          { key: "low", colorPath: "strokeLow.color" },
        ],
      },
      tooltip: tooltipTypes.HighLowMaTooltip,
      description:
        "High & Low: duas médias móveis aritméticas — uma das máximas (MHIGH) e outra das mínimas (MLOW) — com deslocamento vertical percentual opcional. Útil como stop móvel em mercados em tendência. Não confundir com High Low (HHV/LLV).",
    },
    hhv: {
      displayName: "HHV (Highest High Value)",
      key: "hhv",
      type: indicatorTypes.hhv,
      series: seriesTypes.LineSeries,
      coordinate: coordinateTypes.CurrentCoordinate,
      requireNewAxis: true,
      separatePanel: true,
      yAxisId: "HHV",
      defaultOptions: [
        WindowSizeType({
          name: "period",
          displayName: "Período",
          defaultValue: 14,
          min: 1,
          max: 200,
        }),
      ],
      defaultOptionsForAppearance: [
        StrokeType({
          name: "stroke",
          displayName: "Linha",
          defaultValue: {
            color: "#4CAF50",
            width: 1,
            opacity: 1,
            dash: "Solid",
          },
        }),
      ],
      tooltip: tooltipTypes.HHVTooltip,
      renderKey: "hhv",
      yExtents: (plotData, indicator) => {
        const values = plotData
          .map((d) => d.hhv)
          .filter((v) => v !== undefined && v !== null);
        if (values.length === 0) return [0, 1];
        const min = Math.min(...values);
        const max = Math.max(...values);
        const padding = (max - min) * 0.1;
        return [min - padding, max + padding];
      },
      description:
        "HHV (Highest High Value): mostra o máximo do período. HHV = Max(High, período).",
    },
    llv: {
      displayName: "LLV (Lowest Low Value)",
      key: "llv",
      type: indicatorTypes.llv,
      series: seriesTypes.LineSeries,
      coordinate: coordinateTypes.CurrentCoordinate,
      requireNewAxis: true,
      separatePanel: true,
      yAxisId: "LLV",
      defaultOptions: [
        WindowSizeType({
          name: "period",
          displayName: "Período",
          defaultValue: 14,
          min: 1,
          max: 200,
        }),
      ],
      defaultOptionsForAppearance: [
        StrokeType({
          name: "stroke",
          displayName: "Linha",
          defaultValue: {
            color: "#F44336",
            width: 1,
            opacity: 1,
            dash: "Solid",
          },
        }),
      ],
      tooltip: tooltipTypes.LLVTooltip,
      renderKey: "llv",
      yExtents: (plotData, indicator) => {
        const values = plotData
          .map((d) => d.llv)
          .filter((v) => v !== undefined && v !== null);
        if (values.length === 0) return [0, 1];
        const min = Math.min(...values);
        const max = Math.max(...values);
        const padding = (max - min) * 0.1;
        return [min - padding, max + padding];
      },
      description:
        "LLV (Lowest Low Value): mostra o mínimo do período. LLV = Min(Low, período).",
    },
    bollingerBand: {
      displayName: "Bandas de Bollinger (BB)",
      key: "bollingerBand",
      type: indicatorTypes.bollingerBand,
      series: seriesTypes.BollingerSeries,
      coordinate: coordinateTypes.CurrentCoordinate,
      requireNewAxis: false,
      defaultOptions: [
        WindowSizeType({ defaultValue: 20 }),
        SourcePathType({ defaultValue: "close" }),
        MultiplierType({ defaultValue: 2 }),
        MovingAverageType({ defaultValue: "sma" }),
      ],
      defaultOptionsForAppearance: [
        StrokeType({
          name: "strokeTop",
          displayName: "Cor Superior",
          defaultValue: {
            color: "#964B00",
            width: 1,
            opacity: 1,
            dash: "Solid",
          },
        }),
        StrokeType({
          name: "strokeMiddle",
          displayName: "Cor Média",
          defaultValue: {
            color: "#FF0000",
            width: 1,
            opacity: 1,
            dash: "Solid",
          },
        }),
        StrokeType({
          name: "strokeBottom",
          displayName: "Cor Inferior",
          defaultValue: {
            color: "#964B00",
            width: 1,
            opacity: 1,
            dash: "Solid",
          },
        }),
        FillType({
          name: "fill",
          displayName: "Cor Preenchimento",
          defaultValue: { color: "#4682B4", opacity: 0.1 },
        }),
      ],
      labelConfig: {
        lines: [
          { key: "top", colorPath: "strokeTop.color" },
          { key: "middle", colorPath: "strokeMiddle.color" },
          { key: "bottom", colorPath: "strokeBottom.color" },
        ],
      },
      tooltip: tooltipTypes.BollingerBandTooltip,
      renderKey: "bollingerBand",
    },
    // Chaikin's Volatility (migrado do Silverlight FML.VOLATI; a mesma fórmula
    // do calculateChaikinVolatility do ChartIQ). ≠ historicalVolatility.
    chaikinVolatility: {
      displayName: "Volatilidade de Chaikin (VOLATI)",
      key: "chaikinVolatility",
      type: indicatorTypes.chaikinVolatility,
      series: seriesTypes.ChaikinVolatilitySeries,
      coordinate: coordinateTypes.CurrentCoordinate,
      requireNewAxis: true,
      isPriceAxis: false,
      separatePanel: true,
      yAxisId: "ChaikinVolatility",
      renderKey: "chaikinVolatility",
      decimalPlaces: 2,
      defaultOptions: [
        WindowSizeType({
          name: "period",
          displayName: "Período da EMA",
          defaultValue: 10,
          min: 1,
          max: 1000,
        }),
        // O legado usa o mesmo N para a EMA e para a defasagem; o ChartIQ
        // separa os dois — aqui ficam separados (com o mesmo default).
        WindowSizeType({
          name: "rocPeriod",
          displayName: "Período da variação",
          defaultValue: 10,
          min: 1,
          max: 1000,
        }),
      ],
      defaultOptionsForAppearance: [
        StrokeType({
          name: "stroke",
          displayName: "Linha",
          // Cor default do engine (1ª série da paleta por índice).
          defaultValue: {
            color: "#FFFF00",
            width: 1,
            opacity: 1,
            dash: "Solid",
          },
        }),
        StrokeType({
          name: "strokeZeroLine",
          displayName: "Linha Zero",
          defaultValue: {
            color: "#666666",
            dash: "Solid",
            width: 1,
            opacity: 1,
          },
        }),
      ],
      tooltip: tooltipTypes.SingleValueTooltip,
      tooltipProps: {
        yLabel: "VOLATI",
      },
      // Percentual sem limites, simétrico em torno de zero.
      yExtentKeys: ["chaikinVolatility"],
      yExtents: (plotData) => {
        const values = plotData
          .map((d) => d.chaikinVolatility)
          .filter((v) => v !== undefined && v !== null && !isNaN(v));
        if (values.length === 0) return [-1, 1];
        const min = Math.min(...values);
        const max = Math.max(...values);
        const padding = Math.max((max - min) * 0.1, 1);
        return [min - padding, max + padding];
      },
      description:
        "Volatilidade de Chaikin: variação percentual da média exponencial da amplitude (máxima − mínima) em relação a N barras atrás. Alta crescente indica expansão da volatilidade.",
    },
    // CDP — Média HLC e Desvios (migrado do Silverlight FML.CDP, categoria
    // Bandas). Overlay de preço, 5 linhas, sem parâmetros.
    cdp: {
      displayName: "Média HLC e Desvios (CDP)",
      key: "cdp",
      type: indicatorTypes.cdp,
      series: seriesTypes.CDPSeries,
      coordinate: coordinateTypes.CurrentCoordinate,
      requireNewAxis: false,
      isPriceAxis: true,
      drawOnMainChart: true,
      renderKey: "cdp",
      decimalPlaces: 2,
      defaultOptions: [],
      // Paleta default do engine, por ÍNDICE de série no FormulaPackage
      // [CDP, AH, AL, NH, NL] (FormulaBase.DefaultColors).
      defaultOptionsForAppearance: [
        StrokeType({
          name: "strokeCdp",
          displayName: "CDP (média HLC)",
          defaultValue: {
            color: "#FFFF00",
            width: 1,
            opacity: 1,
            dash: "Solid",
          },
        }),
        StrokeType({
          name: "strokeAh",
          displayName: "AH (desvio superior)",
          defaultValue: {
            color: "#FF0000",
            width: 1,
            opacity: 1,
            dash: "Solid",
          },
        }),
        StrokeType({
          name: "strokeAl",
          displayName: "AL (desvio inferior)",
          defaultValue: {
            color: "#0000B4",
            width: 1,
            opacity: 1,
            dash: "Solid",
          },
        }),
        StrokeType({
          name: "strokeNh",
          displayName: "NH",
          defaultValue: {
            color: "#A9A9A9",
            width: 1,
            opacity: 1,
            dash: "Solid",
          },
        }),
        StrokeType({
          name: "strokeNl",
          displayName: "NL",
          defaultValue: {
            color: "#1E90FF",
            width: 1,
            opacity: 1,
            dash: "Solid",
          },
        }),
      ],
      labelConfig: {
        lines: [
          { key: "cdp", colorPath: "strokeCdp.color" },
          { key: "ah", colorPath: "strokeAh.color" },
          { key: "al", colorPath: "strokeAl.color" },
          { key: "nh", colorPath: "strokeNh.color" },
          { key: "nl", colorPath: "strokeNl.color" },
        ],
      },
      tooltip: tooltipTypes.CDPTooltip,
      description:
        "Média HLC e Desvios: a média do preço típico e quatro níveis derivados dela e da amplitude da barra anterior, usados como suporte e resistência.",
    },
    // SR5 — Suporte e Resistência 5 (migrado do Silverlight FML.SR5, categoria
    // Bandas). Overlay de preço, 10 linhas, sem parâmetros.
    //
    // ⚠️ NÃO confundir: `pivotPoints` é por PERÍODO (dia/semana, PP do período
    // anterior, 7 saídas) e `supportResistance` (ex-classe SR) usa só a barra
    // atual, com 1 nível + fill. O SR5 é por BARRA — P da atual, H/L da
    // anterior — e tem cadeia própria em R2..R5/S2..S5.
    sr5: {
      displayName: "Suporte e Resistência 5 (SR5)",
      key: "sr5",
      type: indicatorTypes.sr5,
      series: seriesTypes.SR5Series,
      coordinate: coordinateTypes.CurrentCoordinate,
      requireNewAxis: false,
      isPriceAxis: true,
      drawOnMainChart: true,
      renderKey: "sr5",
      decimalPlaces: 2,
      defaultOptions: [],
      // Paleta default do engine, por ÍNDICE de série no FormulaPackage
      // [R1,S1,R2,S2,R3,S3,R4,S4,R5,S5]. São só 6 cores com ROTAÇÃO
      // (FormulaBase.cs:477-517), então R4/S4/R5/S5 repetem as 4 primeiras —
      // é o que o cliente vê hoje no legado, não descuido.
      defaultOptionsForAppearance: [
        StrokeType({
          name: "strokeR1",
          displayName: "R1 (resistência 1)",
          defaultValue: {
            color: "#FFFF00",
            width: 1,
            opacity: 1,
            dash: "Solid",
          },
        }),
        StrokeType({
          name: "strokeS1",
          displayName: "S1 (suporte 1)",
          defaultValue: {
            color: "#FF0000",
            width: 1,
            opacity: 1,
            dash: "Solid",
          },
        }),
        StrokeType({
          name: "strokeR2",
          displayName: "R2 (resistência 2)",
          defaultValue: {
            color: "#0000B4",
            width: 1,
            opacity: 1,
            dash: "Solid",
          },
        }),
        StrokeType({
          name: "strokeS2",
          displayName: "S2 (suporte 2)",
          defaultValue: {
            color: "#A9A9A9",
            width: 1,
            opacity: 1,
            dash: "Solid",
          },
        }),
        StrokeType({
          name: "strokeR3",
          displayName: "R3 (resistência 3)",
          defaultValue: {
            color: "#1E90FF",
            width: 1,
            opacity: 1,
            dash: "Solid",
          },
        }),
        StrokeType({
          name: "strokeS3",
          displayName: "S3 (suporte 3)",
          defaultValue: {
            color: "#800080",
            width: 1,
            opacity: 1,
            dash: "Solid",
          },
        }),
        StrokeType({
          name: "strokeR4",
          displayName: "R4 (resistência 4)",
          defaultValue: {
            color: "#FFFF00",
            width: 1,
            opacity: 1,
            dash: "Solid",
          },
        }),
        StrokeType({
          name: "strokeS4",
          displayName: "S4 (suporte 4)",
          defaultValue: {
            color: "#FF0000",
            width: 1,
            opacity: 1,
            dash: "Solid",
          },
        }),
        StrokeType({
          name: "strokeR5",
          displayName: "R5 (resistência 5)",
          defaultValue: {
            color: "#0000B4",
            width: 1,
            opacity: 1,
            dash: "Solid",
          },
        }),
        StrokeType({
          name: "strokeS5",
          displayName: "S5 (suporte 5)",
          defaultValue: {
            color: "#A9A9A9",
            width: 1,
            opacity: 1,
            dash: "Solid",
          },
        }),
      ],
      labelConfig: {
        lines: [
          { key: "r1", colorPath: "strokeR1.color" },
          { key: "s1", colorPath: "strokeS1.color" },
          { key: "r2", colorPath: "strokeR2.color" },
          { key: "s2", colorPath: "strokeS2.color" },
          { key: "r3", colorPath: "strokeR3.color" },
          { key: "s3", colorPath: "strokeS3.color" },
          { key: "r4", colorPath: "strokeR4.color" },
          { key: "s4", colorPath: "strokeS4.color" },
          { key: "r5", colorPath: "strokeR5.color" },
          { key: "s5", colorPath: "strokeS5.color" },
        ],
      },
      tooltip: tooltipTypes.SR5Tooltip,
      description:
        "Suporte e Resistência 5: dez níveis de pivô simples (R1–R5 e S1–S5) derivados do preço típico da barra atual e da máxima/mínima da barra anterior.",
    },
    // MIKE — Maiores Altas e Baixas com a Média HLC (migrado do Silverlight
    // FML.MIKE, categoria Bandas). Overlay de preço, 6 linhas.
    mike: {
      displayName: "Maiores Altas e Baixas com a Média HLC (MIKE)",
      key: "mike",
      type: indicatorTypes.mike,
      series: seriesTypes.MIKESeries,
      coordinate: coordinateTypes.CurrentCoordinate,
      requireNewAxis: false,
      isPriceAxis: true,
      drawOnMainChart: true,
      renderKey: "mike",
      decimalPlaces: 2,
      defaultOptions: [
        WindowSizeType({
          name: "period",
          displayName: "Período",
          defaultValue: 12,
          min: 1,
          max: 1000,
        }),
      ],
      // Paleta default do engine, por ÍNDICE de série no FormulaPackage
      // [WR, MR, SR, WS, MS, SS] (FormulaBase.DefaultColors).
      defaultOptionsForAppearance: [
        StrokeType({
          name: "strokeWr",
          displayName: "WR (resistência fraca)",
          defaultValue: {
            color: "#FFFF00",
            width: 1,
            opacity: 1,
            dash: "Solid",
          },
        }),
        StrokeType({
          name: "strokeMr",
          displayName: "MR (resistência média)",
          defaultValue: {
            color: "#FF0000",
            width: 1,
            opacity: 1,
            dash: "Solid",
          },
        }),
        StrokeType({
          name: "strokeSr",
          displayName: "SR (resistência forte)",
          defaultValue: {
            color: "#0000B4",
            width: 1,
            opacity: 1,
            dash: "Solid",
          },
        }),
        StrokeType({
          name: "strokeWs",
          displayName: "WS (suporte fraco)",
          defaultValue: {
            color: "#A9A9A9",
            width: 1,
            opacity: 1,
            dash: "Solid",
          },
        }),
        StrokeType({
          name: "strokeMs",
          displayName: "MS (suporte médio)",
          defaultValue: {
            color: "#1E90FF",
            width: 1,
            opacity: 1,
            dash: "Solid",
          },
        }),
        StrokeType({
          name: "strokeSs",
          displayName: "SS (suporte forte)",
          defaultValue: {
            color: "#800080",
            width: 1,
            opacity: 1,
            dash: "Solid",
          },
        }),
      ],
      labelConfig: {
        lines: [
          { key: "wr", colorPath: "strokeWr.color" },
          { key: "mr", colorPath: "strokeMr.color" },
          { key: "sr", colorPath: "strokeSr.color" },
          { key: "ws", colorPath: "strokeWs.color" },
          { key: "ms", colorPath: "strokeMs.color" },
          { key: "ss", colorPath: "strokeSs.color" },
        ],
      },
      tooltip: tooltipTypes.MIKETooltip,
      description:
        "Maiores Altas e Baixas com a Média HLC: seis níveis de suporte e resistência (fraco, médio e forte) projetados a partir do preço típico e das máximas/mínimas do período.",
    },
    bbiBoll: {
      displayName: "Bullish-Bear Bollinger (BBIBOLL)",
      key: "bbiBoll",
      type: indicatorTypes.bbiBoll,
      series: seriesTypes.BollingerSeries,
      coordinate: coordinateTypes.CurrentCoordinate,
      requireNewAxis: false,
      isPriceAxis: true,
      drawOnMainChart: true,
      renderKey: "bbiBoll",
      defaultOptions: [
        NumericPropertyType({
          name: "period",
          displayName: "Período (N)",
          min: 1,
          max: 1000,
          defaultValue: 10,
        }),
        NumericPropertyType({
          name: "multiplier",
          displayName: "Fator (P)",
          min: 0.1,
          max: 1000,
          defaultValue: 3,
        }),
        SourcePathType({ defaultValue: "close" }),
      ],
      defaultOptionsForAppearance: [
        // Cores default = engine (fórmula não seta SetAttrs; colhidas no OOB vivo):
        // UPR=Red, BBI=Yellow, DWN=#0000B4 (mesmo azul do Center do RSICutler).
        StrokeType({
          name: "strokeTop",
          displayName: "Cor Superior (UPR)",
          defaultValue: {
            color: "#FF0000",
            width: 1,
            opacity: 1,
            dash: "Solid",
          },
        }),
        StrokeType({
          name: "strokeMiddle",
          displayName: "Cor Média (BBI)",
          defaultValue: {
            color: "#FFFF00",
            width: 1,
            opacity: 1,
            dash: "Solid",
          },
        }),
        StrokeType({
          name: "strokeBottom",
          displayName: "Cor Inferior (DWN)",
          defaultValue: {
            color: "#0000B4",
            width: 1,
            opacity: 1,
            dash: "Solid",
          },
        }),
        // Engine plota 3 LINHAS, sem FILLRGN → sem preenchimento (opacity 0).
        FillType({
          name: "fill",
          displayName: "Cor Preenchimento",
          defaultValue: { color: "#4682B4", opacity: 0 },
        }),
      ],
      labelConfig: {
        lines: [
          { key: "top", colorPath: "strokeTop.color" },
          { key: "middle", colorPath: "strokeMiddle.color" },
          { key: "bottom", colorPath: "strokeBottom.color" },
        ],
      },
      tooltip: tooltipTypes.BbiBollTooltip,
      description:
        "Bullish-Bear Bollinger: banda de Bollinger sobre o BBI (média das MAs 3/6/12/24) — UPR/DWN = BBI ± Fator·desvio-padrão do BBI numa janela N. Overlay de preço, 3 linhas.",
    },
    historicalVolatility: {
      displayName: "Volatilidade Histórica (Historical Volatility)",
      key: "historicalVolatility",
      type: indicatorTypes.historicalVolatility,
      series: seriesTypes.LineSeries,
      coordinate: coordinateTypes.CurrentCoordinate,
      requireNewAxis: true,
      separatePanel: true,
      yAxisId: "HistoricalVolatility",
      defaultOptions: [
        WindowSizeType({
          name: "windowSize",
          displayName: "Período",
          defaultValue: 10,
          min: 2,
          max: 100,
        }),
        SourcePathType({
          name: "sourcePath",
          displayName: "Campo",
          defaultValue: "close",
        }),
        NumericPropertyType({
          name: "daysPerYear",
          displayName: "Dias por Ano",
          defaultValue: 252,
          min: 1,
          max: 365,
        }),
        NumericPropertyType({
          name: "standardDeviations",
          displayName: "Desvios Padrão",
          defaultValue: 1,
          min: 0.1,
          max: 5,
          step: 0.1,
        }),
      ],
      defaultOptionsForAppearance: [
        StrokeType({
          name: "stroke",
          displayName: "Linha",
          defaultValue: {
            color: "#2196F3",
            width: 1,
            opacity: 1,
            dash: "Solid",
          },
        }),
      ],
      tooltip: tooltipTypes.HistoricalVolatilityTooltip,
      renderKey: "historicalVolatility",
      yExtents: (plotData, indicator) => {
        const values = plotData
          .map((d) => d.historicalVolatility)
          .filter((v) => v !== undefined && v !== null);
        if (values.length === 0) return [0, 10];
        const min = Math.min(...values);
        const max = Math.max(...values);
        const padding = (max - min) * 0.1 || 1;
        return [Math.max(0, min - padding), max + padding];
      },
      description:
        "Volatilidade Histórica: mede a volatilidade anualizada do ativo calculando o desvio padrão dos retornos logarítmicos. Valores mais altos indicam maior volatilidade.",
    },
    standardDeviation: {
      displayName: "Desvio Padrão (Standard Deviation)",
      key: "standardDeviation",
      type: indicatorTypes.standardDeviation,
      series: seriesTypes.LineSeries,
      coordinate: coordinateTypes.CurrentCoordinate,
      requireNewAxis: true,
      separatePanel: true,
      yAxisId: "StandardDeviation",
      isPriceAxis: false,
      defaultOptions: [
        WindowSizeType({
          name: "windowSize",
          displayName: "Período",
          defaultValue: 14,
          min: 2,
          max: 100,
        }),
        SourcePathType({
          name: "sourcePath",
          displayName: "Campo",
          defaultValue: "close",
        }),
        MovingAverageType({
          name: "movingAverageType",
          displayName: "Tipo de Média",
          defaultValue: "sma",
        }),
        NumericPropertyType({
          name: "standardDeviations",
          displayName: "Desvios Padrão",
          defaultValue: 2,
          min: 0.1,
          max: 10,
          step: 0.1,
        }),
      ],
      defaultOptionsForAppearance: [
        StrokeType({
          name: "stroke",
          displayName: "Linha",
          defaultValue: {
            color: "#9C27B0",
            width: 1,
            opacity: 1,
            dash: "Solid",
          },
        }),
      ],
      tooltip: tooltipTypes.StandardDeviationTooltip,
      renderKey: "standardDeviation",
      yExtents: (plotData) => {
        const values = plotData
          .map((d) => d.standardDeviation)
          .filter((v) => v !== undefined && v !== null);
        if (values.length === 0) return [0, 10];
        const min = Math.min(...values);
        const max = Math.max(...values);
        const padding = (max - min) * 0.1 || 1;
        return [Math.max(0, min - padding), max + padding];
      },
      decimalPlaces: 2,
      description:
        "Desvio Padrão: mede a volatilidade e dispersão dos preços em relação à sua média móvel. Calculado como a raiz quadrada da variância multiplicada pelo número de desvios padrão. Valores mais altos indicam maior volatilidade e dispersão de preços. Sempre positivo, oscilando de 0 a infinito.",
    },
    fractalChaosBands: {
      displayName: "Bandas de Caos Fraccionadas (Fractal Chaos Bands)",
      key: "fractalChaosBands",
      type: indicatorTypes.fractalChaosBands,
      series: seriesTypes.FractalChaosBandsSeries,
      coordinate: coordinateTypes.CurrentCoordinate,
      requireNewAxis: false,
      isPriceAxis: true,
      drawOnMainChart: true,
      defaultOptions: [
        BooleanType({
          name: "channelFill",
          displayName: "Preenchimento",
          defaultValue: true,
          trueLabel: "Sim",
          falseLabel: "Não",
        }),
      ],
      defaultOptionsForAppearance: [
        StrokeType({
          name: "strokeHigh",
          displayName: "Linha Fractal High",
          defaultValue: {
            color: "#4CAF50",
            width: 1,
            opacity: 1,
            dash: "Solid",
          },
        }),
        StrokeType({
          name: "strokeLow",
          displayName: "Linha Fractal Low",
          defaultValue: {
            color: "#F44336",
            width: 1,
            opacity: 1,
            dash: "Solid",
          },
        }),
        FillType({
          name: "fill",
          displayName: "Cor Preenchimento",
          defaultValue: { color: "#8795A1", opacity: 0.1 },
        }),
      ],
      labelConfig: {
        lines: [
          { key: "fractalHigh", colorPath: "strokeHigh.color" },
          { key: "fractalLow", colorPath: "strokeLow.color" },
        ],
      },
      tooltip: tooltipTypes.FractalChaosBandsTooltip,
      renderKey: "fractalChaosBands",
      yExtentKeys: ["fractalHigh", "fractalLow"],
      description:
        "Bandas de Caos Fraccionadas detectam padrões fractais em dados de preço para criar bandas de suporte/resistência em formato de escada. Um fractal high ocorre quando a máxima de uma barra é a mais alta entre 4 barras adjacentes (2 de cada lado). O inverso para fractal low.",
    },
    massIndex: {
      displayName: "Mass Index (Índice de massa)",
      key: "massIndex",
      type: indicatorTypes.massIndex,
      series: seriesTypes.MassIndexSeries,
      coordinate: coordinateTypes.CurrentCoordinate,
      requireNewAxis: true,
      separatePanel: true,
      yAxisId: "MassIndex",
      isPriceAxis: false,
      defaultOptions: [
        NumericPropertyType({
          name: "period",
          displayName: "Período",
          defaultValue: 25,
          min: 5,
          max: 100,
          step: 1,
        }),
        // N1 do engine legado — era fixo em 9 no código (porte ChartIQ).
        NumericPropertyType({
          name: "emaPeriod",
          displayName: "Período da EMA",
          defaultValue: 9,
          min: 2,
          max: 100,
          step: 1,
        }),
        NumericPropertyType({
          name: "bulgeThreshold",
          displayName: "Limite de Reversão",
          defaultValue: 27,
          min: 20,
          max: 35,
          step: 0.1,
        }),
      ],
      defaultOptionsForAppearance: [
        StrokeType({
          name: "stroke",
          displayName: "Linha Mass Index",
          defaultValue: {
            color: "#2196F3",
            width: 2,
            opacity: 1,
            dash: "Solid",
          },
        }),
        StrokeType({
          name: "strokeThresholdLine",
          displayName: "Linha de Limite",
          defaultValue: {
            color: "#FF6B6B",
            width: 1,
            opacity: 0.7,
            dash: "Dash",
          },
        }),
      ],
      tooltip: tooltipTypes.MassIndexTooltip,
      renderKey: "massIndex",
      decimalPlaces: 2,
      description:
        'Mass Index identifica potenciais reversões de tendência através da compressão e expansão do range (High-Low). Desenvolvido por Donald Dorsey, usa EMA dupla do range para detectar "bulges" (valores acima do limite de 27) que sinalizam enfraquecimento da tendência e possível reversão. Baseado na teoria de que reversões são precedidas por expansão do range de preços.',
    },
    // VolHistHisto — Vol. Histórica (Histograma) (migrado do Silverlight
    // FML.VolHistHisto). Histograma COLORSTICK: 2·(HV − MA(HV,M)).
    volHistHisto: {
      displayName: "Vol. Histórica - Histograma (VolHistHisto)",
      key: "volHistHisto",
      type: indicatorTypes.volHistHisto,
      series: seriesTypes.ColoredHistogramSeries,
      coordinate: coordinateTypes.CurrentCoordinate,
      requireNewAxis: true,
      isPriceAxis: false,
      separatePanel: true,
      yAxisId: "VolHistHisto",
      renderKey: "volHistHisto",
      decimalPlaces: 6,
      defaultOptions: [
        NumericPropertyType({
          name: "period",
          displayName: "Período da volatilidade (P)",
          min: 2,
          max: 1000,
          defaultValue: 20,
        }),
        NumericPropertyType({
          name: "m",
          displayName: "Média (M)",
          min: 1,
          max: 1000,
          defaultValue: 5,
        }),
      ],
      defaultOptionsForAppearance: [
        // Histograma COLORSTICK do engine: colore por sinal (>0 / ≤0).
        ColorType({
          name: "increasingColor",
          displayName: "Cor Positiva",
          defaultValue: { color: "#00DD00" },
        }),
        ColorType({
          name: "decreasingColor",
          displayName: "Cor Negativa",
          defaultValue: { color: "#FF0000" },
        }),
        StrokeType({
          name: "strokeZeroLine",
          displayName: "Linha Zero",
          defaultValue: {
            color: "#666666",
            dash: "Solid",
            width: 1,
            opacity: 1,
          },
        }),
      ],
      transformAppearanceConfig: (config) => ({
        ...config,
        increasingColor: config.increasingColor?.color || "#00DD00",
        decreasingColor: config.decreasingColor?.color || "#FF0000",
        strokeZeroLine: config.strokeZeroLine || {
          color: "#666666",
          dash: "Solid",
          width: 1,
          opacity: 1,
        },
      }),
      tooltip: tooltipTypes.VolHistHistoTooltip,
      description:
        "Volatilidade Histórica em histograma: duas vezes a diferença entre a volatilidade histórica anualizada e sua média, colorida por sinal. Destaca a expansão e a contração da volatilidade.",
    },
    // VR — Volatility Ratio (migrado do Silverlight FML.VR, categoria
    // Volatilidade; ≠ relativeVolatility, fórmulas sem relação).
    vr: {
      displayName: "Volatility Ratio (VR)",
      key: "vr",
      type: indicatorTypes.vr,
      series: seriesTypes.LineSeries,
      coordinate: coordinateTypes.CurrentCoordinate,
      requireNewAxis: true,
      separatePanel: true,
      isPriceAxis: false,
      yAxisId: "Vr",
      defaultOptions: [
        WindowSizeType({
          name: "period",
          displayName: "Período",
          defaultValue: 26,
          min: 1,
          max: 1000,
        }),
      ],
      defaultOptionsForAppearance: [
        StrokeType({
          name: "stroke",
          displayName: "Linha",
          // Cor default do engine original (1ª série da paleta por índice).
          defaultValue: {
            color: "#FFFF00",
            width: 1,
            opacity: 1,
            dash: "Solid",
          },
        }),
      ],
      tooltip: tooltipTypes.SingleValueTooltip,
      tooltipProps: {
        yLabel: "VR",
      },
      renderKey: "vr",
      // Auto-fit à faixa real dos dados (o Run() não crava eixo → o engine
      // auto-escala). VR é uma razão ≥ 0 sem teto → só piso em 0.
      yExtentKeys: ["vr"],
      yExtents: (plotData) => {
        const values = plotData
          .map((d) => d.vr)
          .filter((v) => v !== undefined && v !== null && !isNaN(v));
        if (values.length === 0) return [0, 100];
        const min = Math.min(...values);
        const max = Math.max(...values);
        const padding = Math.max((max - min) * 0.1, 1);
        return [Math.max(0, min - padding), max + padding];
      },
      decimalPlaces: 2,
      description:
        "Volatility Ratio compara o volume negociado nas barras de alta com o das barras de baixa dentro do período, destacando rompimentos e dias de forte variação.",
    },
  },
};

export default volatilityRegistry;
